import React, { FC, ReactElement, ReactNode } from 'react';
import Button from '@app/components/Button';
import styled from '@emotion/styled';
import { Checkbox, CircularProgress, FormControlLabel } from '@mui/material';
import { arrayWithoutValue } from '@app/utils/arrayUtils';
import Loadable, { LoadableCreator } from '@app/utils/Loadable';
import InputBox from '@app/components/inputs/inputBox/InputBox';
import NakedFormInput from '@app/components/inputs/NakedFormInput';
import { useLoadableSearchFilter } from '@app/hooks/useSearchFilter';

export interface MultiselectValue {
  text: ReactNode;
  value: string;
  keywords?: string[];
  dividerAfter?: boolean;
}

interface Props {
  values: MultiselectValue[] | Loadable<MultiselectValue[]>;
  selectedKeys: string[];
  setSelectedKeys: (keys: string[]) => void;
  confirm: () => void;
  clearFilters: () => void;
  showSearch?: boolean;
  maxHeight?: string; // Optional prop to set the max height of the popup, default is '95vh'
}

const FilterColumnMultiValuePopup: FC<Props> = ({
  values,
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  showSearch,
  maxHeight = '95vh',
}): ReactElement => {
  const handleKeyPress = async (e: React.KeyboardEvent<HTMLDivElement>): Promise<void> => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirm();
    }
  };

  const valuesLoadable: Loadable<MultiselectValue[]> = LoadableCreator.wrap(values);
  const [filteredData, searchTerm, setSearchTerm] = useLoadableSearchFilter<MultiselectValue>(valuesLoadable, {
    partialMatches(searchItem) {
      return searchItem.keywords ?? [];
    },
  });

  return (
    <PopupContent onKeyPress={handleKeyPress} maxHeight={maxHeight}>
      <Heading>
        <Title>Filter</Title>
        <Button
          appearance='text'
          id='btn-table-multivalue-filter-reset'
          onClick={(): void => {
            setSearchTerm('');
            clearFilters();
          }}
          size='small'
        >
          CLEAR
        </Button>
      </Heading>
      {showSearch && (
        <InputBox>
          <StyledNakedFormInput
            placeholder='Search'
            type='text'
            name='input-table-text-filter'
            dataTestId='input-table-text-filter'
            value={searchTerm}
            onChange={(newValue): void => setSearchTerm(newValue ?? '')}
            autoFocus
          />
        </InputBox>
      )}
      {filteredData.resolve(
        (loadedValues): ReactElement => (
          <>
            <CheckboxList data-testid='cbox-list-all-values'>
              {loadedValues.map((value) => (
                <React.Fragment key={`companyCode-value-${value.value}`}>
                  <StyledFormControlLabel
                    control={
                      <StyledCheckbox
                        id={`cbox-table-multivalue-filter-value-${value.value}`}
                        data-testid={`cbox-table-multivalue-filter-value-${value.value}`}
                        checked={selectedKeys.includes(value.value)}
                        onChange={(newValue): void => {
                          if (newValue.currentTarget.checked) {
                            setSelectedKeys([...selectedKeys, value.value]);
                          } else {
                            setSelectedKeys(arrayWithoutValue(selectedKeys, value.value));
                          }
                        }}
                        color='primary'
                      />
                    }
                    label={value.text}
                  />
                  {value.dividerAfter && <ItemDivider />}
                </React.Fragment>
              ))}
            </CheckboxList>
            <Actions>
              <Button id='btn-table-multivalue-filter-search' size='small' onClick={(): void => confirm()}>
                APPLY
              </Button>
            </Actions>
          </>
        ),
        (): ReactElement => (
          <LoaderConteiner>
            <CircularProgress color='primary' size={32} />
          </LoaderConteiner>
        ),
      )}
    </PopupContent>
  );
};

export default FilterColumnMultiValuePopup;

const PopupContent = styled.div<{ maxHeight?: string }>`
  padding: 10px 12px 10px 18px;
  max-height: ${({ maxHeight }): string => maxHeight ?? '95vh'};

  display: flex;
  flex-direction: column;
`;

const Heading = styled.div`
  flex: 0 0 auto;

  display: flex;
  align-items: center;
  padding-bottom: 10px;
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: normal;
  color: var(--transparent-black-900);

  flex: 1;
`;

const Actions = styled.div`
  flex: 0 0 auto;

  padding-top: 12px;

  display: flex;
  justify-content: flex-end;

  & > *:not(:first-child) {
    margin-left: 8px;
  }
`;

const CheckboxList = styled.div`
  flex: 1;
  overflow-y: scroll;

  display: flex;
  flex-direction: column;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  margin-left: 0;
  font-size: 12px;
  letter-spacing: 0.68px;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-left: -12px;
`;

const ItemDivider = styled.hr`
  border: none;
  border-top: 1px solid var(--transparent-black-200, #e0e0e0);
  margin: 4px 0;
`;

const LoaderConteiner = styled.div`
  padding: 100px;
`;

const StyledNakedFormInput = styled(NakedFormInput)`
  min-width: 300px;
`;
